import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import {motion, useScroll, useSpring, useTransform} from 'motion/react';
import JackpotBanner from '../components/recharge/JackpotBanner';
import Tabs from '../components/recharge/Tabs';
import Top from '../components/recharge/Top';
import LeaderboardList from '../components/recharge/LeaderboardList';
import CurrentUserRank from '../components/recharge/CurrentUserRank';
import {LeaderboardItemType} from '../types';
import DetailNavTitle from '@businessComponents/detail-nav-title';
import {goBack} from '@utils';
import {useRechargeBoard} from '../hooks/useRechargeBoard';
import Rule from '@/common-pages/leaderboard/components/rule';

const defaultUser =
  require('@/common-pages/leaderboard/assets/user-default-2.svg').default;
// 常量配置
const SCROLL_THRESHOLD = 20;
const PULL_DISTANCE_MAX = -150;
const BOUNCE_DISTANCE_MAX = 50;
const SPRING_CONFIG = {
  stiffness: 400,
  damping: 25,
  mass: 0.8,
};

// tabs 配置
const TABS_CONFIG = [
  {
    label: 'Today',
    key: '1',
  },
  {
    label: 'Yesterday',
    key: '2',
  },
];

interface RechargeBoardProps {
  label?: string;
  currentUser?: LeaderboardItemType;
}

function RechargeBoard({
  label = 'Rookie Betting Champions',
  currentUser: propCurrentUser,
}: RechargeBoardProps) {
  const [activeTab, setActiveTab] = useState('1');
  const [dateOffset, setDateOffset] = useState(0); // 0 = 今天, 1 = 昨天
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showHiddenItems, setShowHiddenItems] = useState(false);

  // 使用专门的 RechargeBoard hook
  const {data, loading, error, jackpot, currentUser} =
    useRechargeBoard(dateOffset);
  // 监听滚动进度
  const {scrollY} = useScroll({container: scrollContainerRef});

  // 优化的滚动处理函数
  const handleScroll = useCallback((value: number) => {
    const shouldShow = value > SCROLL_THRESHOLD;
    setShowHiddenItems(prevState => {
      // 只在状态真正改变时更新
      if (prevState !== shouldShow) {
        return shouldShow;
      }
      return prevState;
    });
  }, []);

  // 使用 useEffect 管理监听器，避免内存泄漏
  useEffect(() => {
    const unsubscribe = scrollY.on('change', handleScroll);
    return () => {
      unsubscribe();
    };
  }, [scrollY, handleScroll]);

  // 创建下拉回弹效果 - 当scrollY为负值时生效
  const pullDistance = useTransform(scrollY, value => Math.min(0, value));
  const bounceY = useSpring(
    useTransform(
      pullDistance,
      [PULL_DISTANCE_MAX, 0],
      [BOUNCE_DISTANCE_MAX, 0],
    ),
    SPRING_CONFIG,
  );

  // 使用 hook 返回的 currentUser 或传入的 propCurrentUser 或默认值
  const userRankData = useMemo(() => {
    return (
      currentUser ||
      propCurrentUser || {
        id: '',
        username: 'You',
        avatar: defaultUser as unknown as string,
        rank: 0,
        bonus: 0,
        point: 0,
        userPhone: '',
        rewardRate: 0,
        rewardRateStr: '0%',
      }
    );
  }, [currentUser, propCurrentUser]);

  // Memoize tabs 配置
  const tabItems = useMemo(() => TABS_CONFIG, []);

  // 处理 tab 切换
  const handleTabChange = useCallback((activeKey: string) => {
    setActiveTab(activeKey);
    // 根据 tab 切换日期
    const offset = activeKey === '1' ? 0 : 1; // '1' = 今天, '2' = 昨天
    setDateOffset(offset);
  }, []);

  return (
    <div className="leaderboard-bg relative h-screen flex flex-col overflow-hidden">
      <DetailNavTitle
        title={label}
        hideServer={true}
        hideAmount
        rightNode={<Rule gameName="NEW_USER_RANK_ACTIVITY" />}
        onBack={() => {
          goBack();
        }}
      />
      <div className="relative z-10">
        <div
          className="flex justify-center mt-3 mb-2 bg-inherit"
          role="tablist"
          aria-label="Time period selection">
          <Tabs
            items={tabItems}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </div>

        {/* JackpotBanner - 粘性布局 */}
        <div aria-label="Jackpot information" className="relative">
          <JackpotBanner amount={jackpot > 0 ? jackpot : undefined} />
        </div>
      </div>
      <motion.div
        ref={scrollContainerRef}
        style={{y: bounceY}}
        className="overflow-y-auto touch-pan-y flex-1"
        role="region"
        aria-label="Leaderboard scrollable content">
        <div className="z-10 relative pb-24">
          {loading && data.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
            </div>
          ) : (
            <>
              {/* 只有在有前三名数据时才显示 Top 组件 */}
              {data.slice(0, 3).length > 0 && (
                <motion.div
                  initial={false}
                  animate={
                    showHiddenItems
                      ? {opacity: 0, scale: 0, height: 0}
                      : {opacity: 1, scale: 1, height: 'auto'}
                  }
                  transition={{
                    duration: 0.3,
                    ease: 'easeInOut',
                  }}
                  className="origin-center overflow-hidden">
                  <Top data={data.slice(0, 3)} />
                </motion.div>
              )}
              <LeaderboardList data={data} showHiddenItems={showHiddenItems} />
            </>
          )}

          {/* 错误提示 */}
          {error && (
            <div className="text-center py-4 text-red-400">{error}</div>
          )}
        </div>
      </motion.div>
      <CurrentUserRank currentUser={userRankData} />
    </div>
  );
}

export default RechargeBoard;
